/**
 * 女明星模拟器 · H5 入口
 * 职责：创建 6 个 MVP 面板、面板路由切换、底部导航、EventBus 全局联动
 */
import './ui/ui.css';

import { EventBus, GameEvents } from './core/EventBus';
import { gameManager } from './core/GameManager';
import { gameLoop } from './systems/GameLoop';
import { PlotSystem } from './systems/PlotSystem';
import { saveSystem } from './systems/SaveSystem';
import { GameState } from './systems/GameState';
import { Utils } from './core/Utils';
import { GameData } from './data/GameData';
import { GameConfig } from './core/GameConfig';
import type { AttributeKey } from './data/types';
import { ASSISTANT, assistantAvatarPngSrc } from './data/Assistant';
import { showHint, createBtn, createDiv, createSpan, createInfoTip } from './ui/ui';
import { resetFeedback } from './ui/feedback';
import { initModalKeyboard } from './ui/modal';

import { MainMenuPanel } from './ui/panels/MainMenuPanel';
import { CharacterCreatePanel } from './ui/panels/CharacterCreatePanel';
import { DecisionPanel } from './ui/panels/DecisionPanel';
import { MonthProgressPanel } from './ui/panels/MonthProgressPanel';
import { AwardCeremonyPanel } from './ui/panels/AwardCeremonyPanel';
import { StageStoryPanel } from './ui/panels/StageStoryPanel';
import { GameOverPanel } from './ui/panels/GameOverPanel';
import { CharactersPanel } from './ui/panels/CharactersPanel';
import { WeiboPanel } from './ui/panels/WeiboPanel';
import { AchievementShopPanel } from './ui/panels/AchievementShopPanel';
import { AssistPanel } from './ui/panels/AssistPanel';
import { StylingStudioPanel } from './ui/panels/StylingStudioPanel';
import { HeatChartPanel } from './ui/panels/HeatChartPanel';
import { RankLadderPanel } from './ui/panels/RankLadderPanel';
import { shopSystem } from './systems/ShopSystem';
import { scriptSystem } from './systems/ScriptSystem';
import { normalNpcSystem } from './systems/NormalNpcSystem';
import { awardSystem } from './systems/AwardSystem';
import { heatChartSystem } from './systems/HeatChartSystem';
import { noticeSystem } from './systems/NoticeSystem';
import { assistantSystem } from './systems/AssistantSystem';
import { greetingSystem } from './systems/GreetingSystem';
import { fanSystem } from './systems/FanSystem';
import { icon, type IconName } from './ui/icons';
import { StorageAdapter } from './platform';
import { audioManager } from './audio/AudioManager';
import { warmupAvatarParts, prefetchAvatar, avatarPngSrc, AVATAR_MASTER_PX } from './avatar/gamemount';
import { prefetchImageBytes } from './ui/mediaPrefetch';
import { ContractSignPop } from './ui/ContractSignPop';
import { roleCardHtml } from './ui/roleCard';
import { directSignSystem } from './systems/DirectSignSystem';
import { endorsementSystem, ENDORSE_TITLE_NAMES, BRAND_TIER_NAMES, BRAND_CATEGORY_NAMES } from './systems/EndorsementSystem';
import { varietyPromoSystem } from './systems/VarietyPromoSystem';
import { BrandDef, PendingBrandOffer, PendingVarietyOffer, ScriptDef } from './data/types';
import { buzzOfFame } from './ui/buzzReads';
import type { DialogLine, DialogSpeaker } from './ui/VnDialog';

/**
 * 小地图三屏底图预热（2026-10-29 §3.4cm 四轮，幂等）：
 * `FreeroamMap` 是「第一次进周末才惰性构造」，三张全屏底图到那一刻才发请求——提前把字节拉进 HTTP 缓存，
 * 加上 LQIP 占位底，进周末时既不是白屏、真图也几乎同帧到位。
 */
let freeroamBgsArmed = false;
function prefetchFreeroamBgs(): void {
    if (freeroamBgsArmed) return;
    freeroamBgsArmed = true;
    const start = (): void => {
        prefetchImageBytes('freeroam/map-base.webp');
        prefetchImageBytes('freeroam/b-street.webp');
        prefetchImageBytes('scenes/bg-filmcity.webp');
    };
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(start, { timeout: 4000 });
        return;
    }
    setTimeout(start, 2500);
}

/**
 * 已结识人脉头像预热（2026-10-29 §3.4cm 三轮，幂等）：
 * 在玩家停留的页面空闲期把「人脉页会出现的那批脸」先渲好，进人脉页时 fillAvatarSlot 直接同步命中。
 * 只渲已结识（好感 ≥0）的，避免把全服 NPC 都算一遍；失败静默。
 */
function prefetchMetAvatars(): void {
    const start = (): void => {
        try {
            const st = GameState.getInstance();
            const p = st.player;
            if (!p) return;
            const ids = new Set<string>(GameData.NPCS.map(n => n.id));
            (p.normalNpcs ?? []).forEach(n => ids.add(n.id));
            ids.forEach(id => {
                if (st.getFavor(id) < 0) return;
                const special = GameData.NPC_MAP[id];
                if (special?.avatar) { prefetchAvatar(special.avatar); return; }
                const normal = (p.normalNpcs ?? []).find(n => n.id === id);
                if (normal?.avatar) prefetchAvatar(normal.avatar);
            });
        } catch {
            // 主菜单/建角阶段尚无完整玩家状态：忽略
        }
    };
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(start, { timeout: 2000 });
        return;
    }
    setTimeout(start, 600);
}

/** 面板通用接口 */
export interface Panel {
    readonly element: HTMLElement;
    refresh(): void;
    destroy(): void;
}

/** 面板名称 */
export type PanelName =
    | 'mainMenu'
    | 'characterCreate'
    | 'decision'
    | 'monthProgress'
    | 'awardCeremony'
    | 'stageStory'
    | 'gameOver'
    | 'characters'
    | 'weibo'
    | 'achievementShop'
    | 'stylingStudio';

/** 覆盖层面板（显示时不隐藏底层决策页，作为弹窗浮于其上） */
const MODAL_PANELS: ReadonlySet<string> = new Set(['monthProgress', 'awardCeremony', 'stageStory']);

/** 需要隐藏底部导航的面板 */
const HIDE_TAB_BAR: ReadonlySet<string> = new Set(['mainMenu', 'characterCreate', 'gameOver', 'achievementShop', 'stylingStudio']);

/**
 * 悬浮入口拖动落位（设备级 UI 偏好，⛔ 属于存档 ⇒ `saveSystem.deleteSave()` 清不到它）。
 * 键提到模块作用域是给 `onNewGame()` 复位默认位用的（用户 10-03 令「开新档用默认位置」）。
 */
const FAB_POS_KEY = 'assistantFabPos';

export class GameApp {
    private app: HTMLDivElement;
    private shell: HTMLDivElement;
    private panelContainer: HTMLDivElement;
    private tabBar: HTMLElement;
    private tabItems: Record<string, HTMLElement> = {};
    private panels: Record<PanelName, Panel>;
    private current: PanelName | null = null;
    private assistFab: HTMLElement | null = null;
    /** 本次指针序列是否发生过拖拽：吞掉拖完松手时补发的 click，避免误开对话 */
    private fabDidDrag = false;
    /** 拖拽落位重钳函数：FAB 隐藏时 offsetWidth/导航高都是 0，钳位必须在可见后重跑（59 批） */
    private fabReclamp: (() => void) | null = null;
    /** 把入口内联落位整组清掉、交回 CSS 默认（开新档复位用；⛔ 只清 Storage——内联是 init 时还原上去的） */
    private fabReset: (() => void) | null = null;
    /** 颁奖夜播完（关掉回决策页）之后要补做的动作——确认行程链：播报 → 颁奖夜 → 月度执行弹窗 */
    private afterCeremony: (() => void) | null = null;
    /**
     * 覆盖层宿主＝应用外壳（`.game-shell`）。挂在它上面的 `.modal-wrap` 才盖得住底部导航条：
     * 面板根在 `.panel-container` 里，而导航条是它的兄弟节点、无 z、DOM 靠后，同档比树序就把遮罩压在导航底下
     * （尺见 ui.css「层叠收口」那段最后一条）。热度榜、咖位阶梯一直走这里；
     * 2026-10-16 弹窗统一批把剧情卡（MediaPanel）也从三块面板根里挪上来，治「剧情私信弹窗底下导航栏还按得动」。
     * 时序前提＝外壳在构造期先 append（下面 constructor 里早于 `new panels`），面板构造时才取得到。
     */
    get overlayRoot(): HTMLElement { return this.shell; }
    /** 小助理面板——决策页剧情演出档复用其 VnDialog 实例（同一对话框、同一位置） */
    readonly assistPanel: AssistPanel = new AssistPanel();
    /** 全网影视热度榜弹窗（heatchart 批）：独立只读覆盖层，同 AssistPanel 实绩弹窗路数，⛔ 不入 panels 注册表 */
    private heatChartPanel: HeatChartPanel = new HeatChartPanel();
    /** 咖位阶梯弹窗（2026-10-02）：点顶栏咖位徽章开，只读，同榜单/实绩路数，⛔ 不入 panels 注册表 */
    private rankPanel: RankLadderPanel = new RankLadderPanel();
    /**
     * 签约弹窗（2026-10-30 签约呈现改造批）：两档（重档角色卡/轻档戏票）同一张壳，挂外壳顶层。
     * 直签送达链的重档由本文件开；竞角 5c 的轻档由 MonthProgressPanel 自己持一份。
     */
    private signPop!: ContractSignPop;
    /** 决策页入场送达链：演出期间的重入闸（VN 不阻断页面点击，期间再进决策页⛔ 起第二条链） */
    private deliverBusy = false;
    /** 送达链全部消费完后要兑现的续动作（点「确认行程」那条链＝开月度执行弹窗） */
    private deliverDone: (() => void) | null = null;
    /** 经纪人（林静）说话人：直签送达链与商务被动通报共用，头像按捏脸 PNG 异步补 */
    private readonly managerSpeaker: DialogSpeaker = {
        id: GameData.MANAGER.id, name: GameData.MANAGER.name, avatarChar: '林',
    };
    /** 小助理（夏夏）说话人：剧宣递约/录制回执共用，头像启动期异步补（实机抓过「漏头像」，同 manager 修法） */
    private readonly assistantSpeaker: DialogSpeaker = {
        id: ASSISTANT.id, name: ASSISTANT.name, avatarChar: '夏',
    };

    constructor() {
        this.app = document.getElementById('app') as HTMLDivElement;
        this.app.innerHTML = '';

        // 应用外壳：面板容器 + 底部导航
        this.shell = document.createElement('div');
        this.shell.className = 'game-shell';

        this.panelContainer = document.createElement('div');
        this.panelContainer.className = 'panel-container';

        this.tabBar = this.buildTabBar();

        for (const name of ['mainMenu', 'characterCreate', 'decision', 'monthProgress', 'gameOver', 'characters', 'weibo']) {
            const tab = this.tabItems[name];
            if (tab) tab.addEventListener('click', () => this.onTabClick(name));
        }

        this.shell.appendChild(this.panelContainer);
        this.shell.appendChild(this.tabBar);
        this.app.appendChild(this.shell);

        this.panels = {
            mainMenu: new MainMenuPanel(this),
            characterCreate: new CharacterCreatePanel(this),
            decision: new DecisionPanel(this),
            monthProgress: new MonthProgressPanel(this),
            awardCeremony: new AwardCeremonyPanel(this),
            stageStory: new StageStoryPanel(this),
            gameOver: new GameOverPanel(this),
            characters: new CharactersPanel(this),
            weibo: new WeiboPanel(this),
            achievementShop: new AchievementShopPanel(this),
            stylingStudio: new StylingStudioPanel(this),
        };
        // 小助理菜单里的「造型工作室」＝切到整页面板（run 在菜单纸卡收起后才跑，⛔ 两层浮层同帧抢屏）
        // 来路页要在此之前记下：show() 一执行 current 就变成工作室本身，refresh() 里再问就问不到了
        this.assistPanel.openStudio = () => {
            const studio = this.panels.stylingStudio as StylingStudioPanel;
            studio.enteredFrom = this.current ?? 'decision';
            this.show('stylingStudio');
        };

        // 挂载全部面板到容器
        Object.values(this.panels).forEach(p => this.panelContainer.appendChild(p.element));
        // 月度执行弹窗覆盖层：挂到应用外壳顶层（非 panel-container），其透明根 + dimoverlay 的
        // backdrop-filter 同时模糊决策页与底部导航，形态沿自己归档原型 ui-concept/月度执行.html（2026-10-24 归档）的 bg-blur 全屏模糊
        this.shell.appendChild(this.panels.monthProgress.element);
        this.panels.monthProgress.element.classList.add('z-month-top');
        // 颁奖夜同档（盖过决策页与底部导航；当面对话浮层挂在本面板内，演出时压住结果卡）
        this.shell.appendChild(this.panels.awardCeremony.element);
        this.panels.awardCeremony.element.classList.add('z-month-top');
        // 阶段审查演出同档：全屏场景盖住决策页与底部导航，演完由面板自己退回决策页
        this.shell.appendChild(this.panels.stageStory.element);
        this.panels.stageStory.element.classList.add('z-month-top');
        // 热度榜弹窗（heatchart 批）：modal-wrap 自带层级，独立于面板路由。展示时点=行程结束之后、
        // 小助理播报之前（2026-09-30 用户定）：关榜 → 入场链 ④ 开口播报 → ⑤ 尾链
        this.shell.appendChild(this.heatChartPanel.element);
        this.heatChartPanel.onClose = () => this.decisionAfterChart();

        // 咖位阶梯弹窗（2026-10-02）：入口＝顶栏 .level-badge，三页（行程/人脉/微博）同绑一条委托监听。
        // 只读覆盖层，同实绩/榜单路数：开窗收起悬浮入口与当面对话（⛔ 两层浮层同帧抢屏），关窗交还。
        this.shell.appendChild(this.rankPanel.element);
        this.rankPanel.onOpenChanged = (open) => {
            this.assistFab?.classList.toggle('pop-hidden', open);
            if (open) this.assistPanel.hideVn(); else this.assistPanel.resumeVn();
        };
        // 签约弹窗（2026-10-30）：挂外壳（盖住底部导航）与弹窗 z 同档族；强制二选一、点遮罩不关
        this.signPop = new ContractSignPop(this.shell);
        void avatarPngSrc(GameData.MANAGER.avatar, AVATAR_MASTER_PX).then(src => {
            this.managerSpeaker.avatarSrc = src;
        });
        // 小助理头像同款预载（剧宣递约/录制回执的 VN 卡用；实机抓过「夏夏漏头像」）
        void assistantAvatarPngSrc().then(src => {
            this.assistantSpeaker.avatarSrc = src;
        }).catch(() => { });
        this.shell.addEventListener('click', (e) => {
            const badge = (e.target as HTMLElement | null)?.closest?.('.level-badge');
            if (badge) this.rankPanel.open();
        });

        // H-47：弹窗键盘边界（焦点陷阱 / Esc / 焦点归还）。靠观察 .show 生效，面板无需各自接线
        initModalKeyboard(this.app);

        this.initAssistant();

        this.wireEvents();
    }

    /**
     * 小助理：初始化系统 + 挂载悬浮入口 + 挂载当面对话浮层（功能菜单与「查看实绩」弹窗由 AssistPanel 持有）。
     * 悬浮入口：右下角，游戏会话中可见；月度执行弹窗/结算页/对话框或功能弹窗展开时隐藏。
     */
    private initAssistant(): void {
        assistantSystem.init();

        // —— 悬浮入口（右下角，点开＝叫小助理过来给一份功能菜单，⛔ 信箱也不是、闲聊也不是） ——
        const fab = document.createElement('button');
        fab.type = 'button';
        fab.className = 'assistant-fab';
        fab.title = `叫${ASSISTANT.name}来办事`;
        fab.setAttribute('aria-label', `叫${ASSISTANT.name}来办事`);
        // 72 批（用户点单）：悬浮入口不再用 PNG 立绘，图标用 megaphone 喇叭对准「播报」语义；VN 贴纸头像仍用 PNG
        // 73 批（用户点单）：点了不再搭闲话，展开功能菜单（查看实绩已实装，网友评价/业内资讯待实装）⇒ 入口文案同步改
        fab.appendChild(icon('megaphone', 1.7));
        fab.addEventListener('click', () => {
            if (this.fabDidDrag) { this.fabDidDrag = false; return; }
            this.assistPanel.openPanel();
        });
        this.shell.appendChild(fab);
        this.assistFab = fab;
        this.makeFabDraggable(fab);

        // 对话浮层：挂到应用外壳顶层（absolute 铺底、按钮之上、月度执行弹窗之下）
        this.assistPanel.initForGame(this.shell, fab);

        // 5c 队列弹窗宿主注入（决策页背景版）；队列全弹完后跑收口夜乙段（派角）与押后的判定
        const mp = this.panels.monthProgress as MonthProgressPanel;
        mp.attachRevealHost(this.shell);
        mp.onRevealDone = () => {
            // 2026-11-03 竞角重排批：月结（换月+月初掷点+存盘）已挪到队列之前跑完，
            // 这里只剩收口夜乙段（玩家表态完了才派剩下的位）与押后的阶段审查/结局判定。
            gameLoop.finishClosingNight();
            if (this.currentPanel !== 'gameOver') this.show('decision');
        };
    }

    /**
     * 59 批：悬浮入口可自由拖动——位移 >6px 记为拖拽（松手不弹对话），
     * 位置钳进外壳（不压底部导航/出屏），落位存 Storage，下次开局还原；窗口变化重钳。
     *
     * ⚠️ 10-03 修「读档后按钮自己跑到左上角」（用户实测）——旧写法三处⛔ 校验数值：
     * ① 存的是 `{"left":null,"top":null}` 时 `Math.max(null,8)` 把 null 当 0 ⇒ 钳到下限 (8,8)；
     * ② 缺键（`{}`）算出 NaN ⇒ 内联 `left/top` 被浏览器判非法丢弃，可同一句已把 `right/bottom` 置 auto
     *    ⇒ CSS 默认锚点（右下）失效 ⇒ 退回**静态位置 (0,0)**；
     * ③ `endDrag` 用 `parseFloat(fab.style.left)` 回写 ⇒ 内联为空时写出 NaN ⇒ `JSON.stringify` 变 `null`
     *    ⇒ **一次坏帧永久毒化**，玩家自己也拖不回来。两条都⛔ 报错，看着就像"自动"。
     * ⇒ 现在：还原前验有限数（坏值就地清键、回落 CSS 默认）、钳位算不出合法值就交回 CSS、
     *   上限兜一层下限（FAB 隐藏时 offsetWidth/导航高量到 0）、落盘只写有限数。
     */
    private makeFabDraggable(fab: HTMLElement): void {
        const EDGE = 8;
        /** 只认有限数（`null`/缺键/布尔都⛔ 算 0；字符串数字仍收，兼容旧档） */
        const finite = (v: unknown): number | null =>
            (typeof v === 'number' || typeof v === 'string') && Number.isFinite(Number(v)) ? Number(v) : null;
        /** 合法落位 ⇒ 返回钳好的坐标；⛔ 合法值 ⇒ 返回 null（调用方回落 CSS 默认位） */
        const clampPos = (left: number, top: number): { left: number; top: number } | null => {
            if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
            const s = this.shell.getBoundingClientRect();
            const barH = this.tabBar.classList.contains('hidden') ? 0 : this.tabBar.getBoundingClientRect().height;
            // 上限⛔ 能小于下限：量到零尺寸（壳未布局/FAB display:none）时减法会翻负，Math.min 就直接选中负数上限
            const maxLeft = Math.max(EDGE, s.width - fab.offsetWidth - EDGE);
            const maxTop = Math.max(EDGE, s.height - fab.offsetHeight - barH - EDGE);
            return {
                left: Math.min(Math.max(left, EDGE), maxLeft),
                top: Math.min(Math.max(top, EDGE), maxTop),
            };
        };
        /** 四个内联值一起改：要么 left/top 生效并关掉 right/bottom，要么全清（⛔ 半套＝失去锚点掉到 (0,0)） */
        const put = (pos: { left: number; top: number } | null): void => {
            if (!pos) {
                fab.style.left = fab.style.top = fab.style.right = fab.style.bottom = '';
                return;
            }
            fab.style.left = `${pos.left}px`;
            fab.style.top = `${pos.top}px`;
            fab.style.right = 'auto';
            fab.style.bottom = 'auto';
        };

        const saved = StorageAdapter.getItem(FAB_POS_KEY);
        if (saved) {
            let left: number | null = null, top: number | null = null;
            try {
                const pos = JSON.parse(saved) as { left?: unknown; top?: unknown };
                left = finite(pos.left); top = finite(pos.top);
            } catch { /* 整条坏 JSON ⇒ 走清键那条路 */ }
            if (left === null || top === null) {
                StorageAdapter.removeItem(FAB_POS_KEY);   // 坏档就地清掉，⛔ 每次读档都左上角（自愈）
            } else {
                put(clampPos(left, top));                 // 视口可能已变，还原后过一遍钳位
            }
        }

        let dragging = false, moved = false, startX = 0, startY = 0, originLeft = 0, originTop = 0;
        fab.addEventListener('pointerdown', (e) => {
            const s = this.shell.getBoundingClientRect();
            const r = fab.getBoundingClientRect();
            dragging = true; moved = false; this.fabDidDrag = false;
            startX = e.clientX; startY = e.clientY;
            originLeft = r.left - s.left; originTop = r.top - s.top;
            fab.setPointerCapture(e.pointerId);
        });
        fab.addEventListener('pointermove', (e) => {
            if (!dragging) return;
            const dx = e.clientX - startX, dy = e.clientY - startY;
            if (!moved && Math.hypot(dx, dy) < 6) return;
            moved = true; this.fabDidDrag = true;
            put(clampPos(originLeft + dx, originTop + dy));
        });
        const endDrag = () => {
            dragging = false;
            if (!moved) return;
            const left = finite(fab.style.left ? parseFloat(fab.style.left) : null);
            const top = finite(fab.style.top ? parseFloat(fab.style.top) : null);
            if (left === null || top === null) {
                StorageAdapter.removeItem(FAB_POS_KEY);   // 这一帧本就不可信，⛔ 把 NaN 写成 null 毒化以后每一次还原
            } else {
                StorageAdapter.setItem(FAB_POS_KEY, JSON.stringify({ left, top }));
            }
        };
        fab.addEventListener('pointerup', endDrag);
        fab.addEventListener('pointercancel', endDrag);
        this.fabReclamp = () => {
            if (!fab.style.left || !fab.style.top) return;
            put(clampPos(parseFloat(fab.style.left), parseFloat(fab.style.top)));
        };
        this.fabReset = () => put(null);
        window.addEventListener('resize', () => this.fabReclamp?.());
    }

    /** 构建底部导航（行程/人脉/微博） */
    private buildTabBar(): HTMLElement {
        const bar = document.createElement('nav');
        bar.className = 'tab-bar';
        bar.id = 'tab-bar';
        const items: [string, string][] = [
            ['行程', 'decision'],
            ['人脉', 'characters'],
            ['微博', 'weibo'],
        ];
        // 底部导航图标（2026-11-03 用户令）：照 ui-makeover-compare 样张的日历/双人/气泡，竖排图标在文字上方
        const TAB_ICONS: Record<string, IconName> = {
            decision: 'calendar',
            characters: 'users',
            weibo: 'messageSquareText',
        };
        for (const [label, key] of items) {
            const item = createBtn('tab-item');
            item.dataset.tab = key;
            const tabIcon = icon(TAB_ICONS[key]);
            tabIcon.classList.add('tab-icon');
            const tabLabel = document.createElement('span');
            tabLabel.className = 'tab-label';
            tabLabel.textContent = label;
            item.appendChild(tabIcon);
            item.appendChild(tabLabel);
            if (key === 'decision') item.classList.add('active');
            bar.appendChild(item);
            this.tabItems[key] = item;
            if (key === 'characters') {
                // 人脉未读胶囊徽章（有未读经纪人通知时显示，进聊天页后清除）
                const dot = document.createElement('span');
                dot.className = 'tab-badge';
                dot.id = 'characters-tab-badge';
                item.appendChild(dot);
            }
            if (key === 'weibo') {
                // 剧宣角标（2026-10-02 甲案）：本月有可宣在播剧时亮「N部可宣」，发完即隐；.mint＝薄荷底（10-03 用户选定，
                // ⛔ 不占「未读＝朱红」告警语义）
                const dot = document.createElement('span');
                dot.className = 'tab-badge mint';
                dot.id = 'weibo-tab-badge';
                item.appendChild(dot);
            }
        }
        return bar;
    }

    /** 底部「人脉」未读胶囊徽章显隐：经纪人未读 + NPC 打招呼未读 → 计数显示 */
    private updateCharactersBadge(): void {
        const dot = this.tabBar.querySelector('#characters-tab-badge');
        if (!dot) return;
        const player = GameState.getInstance().player;
        if (!player) return; // 主菜单阶段尚未开局
        const noticeUnread = player.managerNotices.length - (player.lastReadNoticeCount ?? 0);
        const greetUnread = greetingSystem.totalUnread();
        // 剧情聊天消息（plotChats）2026-10-14 起计入：message 载体的剧情已改投人脉聊天页（茄子定），
        // 点进去就能读——2026-10-11 那道「⛔ 并入」禁令的前提（收件箱 UI 缓建、点不进去的红点）已不成立。
        const unread = Math.max(0, noticeUnread) + greetUnread + PlotSystem.getInstance().totalUnreadChats();
        const has = unread > 0;
        dot.classList.toggle('show', has);
        dot.textContent = has ? String(unread) : '';
    }

    /** 底部「微博」剧宣角标（2026-10-02 甲案）：本月有可宣在播剧时亮「N部可宣」，全宣完/未开播/主菜单即隐 */
    updateWeiboBadge(): void {
        const dot = this.tabBar.querySelector('#weibo-tab-badge');
        if (!dot) return;
        const player = GameState.getInstance().player;
        if (!player) { dot.classList.remove('show'); dot.textContent = ''; return; }
        const n = fanSystem.promotableScripts().length;
        dot.classList.toggle('show', n > 0);
        dot.textContent = n > 0 ? `${n}部可宣` : '';
        (dot as HTMLElement).title = n > 0 ? `有 ${n} 部在播剧本月可宣传` : '';
    }

    /** 面板切换 */
    show(name: PanelName): void {
        const panel = this.panels[name];
        if (!panel) return;

        // 头像预热（2026-10-29 §3.4cm 三轮）：切屏前先把头像 PNG 备进同步缓存，
        // 顶栏主角头像/人脉列表才能在 fillAvatarSlot 里「同步命中」，⛔ 白一帧再出图
        prefetchAvatar(GameState.getInstance().player?.avatar);
        prefetchMetAvatars();

        // 进入决策页时清空选中状态（下个月/从其他页面回来）
        if (name === 'decision') {
            (this.panels.decision as any).selId = null;
        }

        if (MODAL_PANELS.has(name)) {
            // 覆盖层：保留底层决策页可见，弹窗浮于其上
            this.panels.decision.element.classList.remove('hidden-panel');
            this.panels[name].element.classList.remove('hidden-panel');
        } else {
            Object.values(this.panels).forEach(p => { p.element.classList.add('hidden-panel'); });
            this.panels[name].element.classList.remove('hidden-panel');
        }
        this.current = name;

        // 音频收口（2026-10-24 音频批）：BGM 按场景映射切换（键没变 no-op，同面板重复 show 不重头播）。
        // 覆盖层打开**不配音效**（用户 2026-10-25 逐个试听后定：弹窗声去掉，只留点击与确认两个）。
        // 首次解锁之前这里不尝试播放（音频层只记 pending，控制台不报错）
        audioManager.onPanelShow(name);

        // 底部导航状态：主菜单/建角/结算隐藏，其余显示（行程/月进度/人脉/微博）
        if (HIDE_TAB_BAR.has(name)) {
            this.tabBar.classList.add('hidden');
        } else {
            this.tabBar.classList.remove('hidden');
        }
        // 悬浮入口：月度执行弹窗 / 主菜单 / 建角 / 结算页隐藏；其余显示
        if (this.assistFab) {
            const hideFab = MODAL_PANELS.has(name) || HIDE_TAB_BAR.has(name);
            this.assistFab.classList.toggle('hidden', hideFab);
            if (!hideFab) this.fabReclamp?.(); // 可见后才有真实尺寸/导航高，补钳一次
            if (MODAL_PANELS.has(name)) {
                // 弹窗接管屏幕：对话整段收起且不记已读
                this.assistPanel.hideVn();
            } else {
                // 回到日常页：只解除抑制，⛔ 在这里开播——播报时点交给决策页入场链 ④（热度榜之后）
                this.assistPanel.releaseVn();
            }
        }
        this.setActiveTab(name);

        panel.refresh();
        // 颁奖夜：结算发生在决策日开始时（生涯最后一届）或玩家点「确认行程」时（批 G 的其余届），
        // 界面都要等玩家真正站上决策页才播。单一触发点 ⇒ 新开局 / 读档 / 月结算返回 / 关掉颁奖卡
        // 都会自动补播「结算了但没看过」的那一届，⛔ 另开事件
        if (name === 'decision') {
            // 决策页入场链，顺序固定（2026-10-31 抢跑修复后收敛成一条）：
            //   ① 阶段审查演出（整屏，演完由 StageStoryPanel 退回本页重入）
            //   ② 入场数值/舆论事件档（媒体卡，2026-10-22 令：在影视热度榜之前）
            //   ③ 影视热度榜（decisionEntry）
            //   ④ 小助理播报（榜单关掉之后才开口，播完才续链）
            //   ⑤ 尾链：颁奖夜 → 结局判定 → 送达链（decisionTail）
            // ⛔ 任何一环都不在 show() 里抢跑开播（旧写法在这里 resumeVn，播报先上屏又被 ②③ 的
            //    hideVn 压掉，玩家看到「弹一下又消失」，见 2026-10-31 实测）。
            // 阶段审查演出（2026-10-21 茄子令「审查剧情要带背景图」）：先扫本宿主那一片队列，
            // 有戏就整屏先演（开局立约、25 岁通过回响），演完由 StageStoryPanel 退回本页重入。
            // 失败局的终局在演出排干后才落，所以「有待演标记但事件已不在队里」时在这里补结。
            PlotSystem.getInstance().checkStageStory();
            if (PlotSystem.getInstance().pendingFor('stageStory').length) { this.show('stageStory'); return; }
            if (gameLoop.resolvePendingStageFail()) return;
            // 决策页**入场事件档**（宿主 decisionEntry；2026-10-22 用户令「每个月执行完行程之后在决策页触发，
            // 在每月的影视热度表之前」）：数值/舆论类事件演完才轮到影视热度榜 ⇒ 顺序是结构性保证，不是特判。
            if ((this.panels.decision as DecisionPanel).hasEntryEvents()) {
                // 媒体卡与小助理播报是两层浮层，⛔ 同屏抢读：演之前整段收起（不记已读），
                // 演完交给 decisionEntry() 从 ③ 继续（播报在 ④ 才开口）。
                this.assistPanel.hideVn();
                (this.panels.decision as DecisionPanel).playEntryEventsThen(() => this.decisionEntry());
                return;
            }
            this.decisionEntry();
        }
        // 切换面板时同步人脉未读红点与微博剧宣角标
        this.updateCharactersBadge();
        this.updateWeiboBadge();
    }

    /**
     * 确认行程链·品牌官宣档（2026-10-30 茄子令「确认行程之后、月度执行之前在决策页弹出」）：
     * 挂在 `flushAssistantThen` 的续动作位——小助理播报/颁奖夜讲完 → 弹官宣微博卡（与特殊剧情 media 同一只
     * MediaPanel）→ 全弹完才进决策剧情/月度执行。签约当刻只入队、⛔ 不弹（当月签约也由「确认行程」这一步放行）；
     * 数据缺失＝静默丢这一条继续。
     */
    private playAnnounceThen(done: () => void): void {
        const a = endorsementSystem.consumeAnnounce();
        if (!a) { done(); return; }
        const card = endorsementSystem.announceMedia(a);
        if (!card) { this.playAnnounceThen(done); return; }
        // 媒体卡与小助理播报是两层浮层，⛔ 同屏抢读——弹之前整段收起（不记已读），全弹完再恢复
        this.assistPanel.hideVn();
        const media = { ...card, buzz: buzzOfFame(GameState.getInstance().getFame()) };
        (this.panels.decision as DecisionPanel).showAnnounceMedia(media, () => this.playAnnounceThen(() => {
            this.assistPanel.resumeVn();
            done();
        }));
    }

    /**
     * 决策页入场链 ③：影视热度榜定格 ＋ 弹榜。
     * 展示时点（2026-09-30 用户定，2026-10-31 方案 B 复核）= 行程结束之后、小助理播报之前。
     * 有未看榜 ⇒ 先弹榜（播报此时还没开口，⛔ 需要 hideVn）；空榜月/已看过 ⇒ 直接进 ④ 播报。
     */
    private decisionEntry(): void {
        if (this.current !== 'decision') return;
        heatChartSystem.publishDueChart();
        if (heatChartSystem.unseenChart()) {
            this.heatChartPanel.open();
            return;
        }
        this.decisionAfterChart();
    }

    /** 决策页入场链 ④：小助理播报（榜关掉之后才开口）→ 讲完交 ⑤ 尾链。
     *  ⚠️ 这里必须自带 `releaseVn()`：② 入场事件档演媒体卡前会 `hideVn()`（suppressed=true），
     *  不解除则 flushThen 把播报整段跳过、直接进尾链 —— 玩家要切页再回来才收到通知
     *  （2026-10-31 特训 tag / 咖位晋升播报实测）。 */
    private decisionAfterChart(): void {
        if (this.current !== 'decision') return;
        this.assistPanel.releaseVn();
        this.assistPanel.flushThen(() => this.decisionTail());
    }


    /** 决策日「确认行程」用：小助理先把话讲完（含月初的提名播报）→ 该颁的届先播颁奖夜 → 品牌官宣卡 → 再进月度执行弹窗 */
    flushAssistantThen(done: () => void): void {
        this.assistPanel.flushThen(() => this.ceremonyThen(() => this.playAnnounceThen(done)));
    }

    /**
     * 颁奖夜排在月度执行弹窗之前（批 G，2026-09-30：评选届的结算与演出一起挪到「行程开始前」）。
     * 玩家 12 月月初点确认听「你入围了」，1 月点确认看颁奖夜 ⇒ 中间隔着完整的、可玩的 12 月；
     * 关掉颁奖卡回到决策页时由 `show('decision')` 尾部兑现续动作。
     */
    private ceremonyThen(done: () => void): void {
        if (!awardSystem.unseenCeremony()) { done(); return; }
        this.afterCeremony = done;
        this.show('awardCeremony');
    }

    /**
     * 决策页入场链 ⑤（榜单与播报都给完之后才走这里）：
     * ① 剧情届/生涯最后一届的颁奖夜——这两类只在决策日当场结算（AwardSystem.onMonthStart），
     *    批 B「结局年那届当场先颁完再判退役」的硬顺序 ⛔ 挪去确认链；其余评选届在 onMonthConfirm
     *    （点「确认行程」）结算颁奖，不走本分支；
     * ② 被颁奖夜挡在后面的结局判定（40 岁那个月 = 生涯最后一届的颁奖月）先补跑；
     * ③ 入场送达链（2026-10-30 签约呈现改造批）：待签戏约/经纪人通报先演完，再兑现续动作；
     * ④ 兑现"看完颁奖夜之后"的续动作（点确认行程 → 开月度执行弹窗）。
     * 玩家已切走页签（榜单开着逛微博后关榜等）⇒ 本轮作废，回决策页重入会重新补播，self-healing。
     */
    private decisionTail(): void {
        if (this.current !== 'decision') return;
        if (awardSystem.unseenCeremony()) { this.show('awardCeremony'); return; }
        if (gameLoop.settleEndingIfDue()) { this.afterCeremony = null; return; }
        const then = this.afterCeremony;
        this.afterCeremony = null;
        this.deliverPendingOnEntry(then);
    }

    /**
     * 决策页入场送达链——**唯一消费入口**（2026-10-30 签约呈现改造批，茄子令）。
     * 顺序固定，一类演完回来接着消费下一类，全部空了才兑现 `done`（「确认行程」的续动作）。
     * **类别优先级（2026-11-03 茄子令）＝商务 → 综艺 → 戏约**：商务域（通报+邀约）全部处理完才进综艺，
     * 综艺域处理完才进戏约——⛔ 角色递约不得夹在商务到期通报与商务邀约之间。
     *   ① 商务：被动通报（解约/约满/晋升）→ 待签 offer（EndorsementSystem）；
     *   ② 综艺：录制完成通报 → 剧宣待排期（VarietyPromoSystem，放弃不拉黑）；
     *   ③ 戏约：直签待签 offer（DirectSignSystem）→ 5c 竞角队列。
     *
     * 【丙工位扩展点】①② 归丙，分支只加在本函数与 deliverStep 里；⛔ 改 app.ts / DecisionPanel
     * 的其他结构、⛔ 另立消费入口（两边各消费一次会把待签/通报吃掉一半）。②落地前 currentOffer
     * 非 null 只是跳过——待签数据原地留着，⛔ 丢弃。
     */
    private deliverPendingOnEntry(done: (() => void) | null): void {
        if (this.deliverBusy) {
            if (this.signPop.isOpen) { if (done) this.deliverDone = done; return; }
            // 上一轮链被切页/整段收起打断（VnDialog.stop 不回调 onEnd）：本轮重开，续动作取最新那次
            this.deliverBusy = false;
        }
        this.deliverBusy = true;
        this.deliverDone = done;
        this.deliverStep();
    }

    /** 送达链一步：① 商务（通报→邀约）→ ② 综艺（通报→剧宣）→ ③ 戏约（直签→竞角），严格按域清空再进下域 */
    private deliverStep(): void {
        if (this.current !== 'decision') { this.finishDeliver(); return; }
        // ① 商务：先播旧约收尾通报（到期/解约/晋升），再送新邀约
        //   （2026-10-30 茄子实测纠序：月结入队的「旧约到期」通报先于月初掷点的「新邀约」送达；
        //    2026-11-03 茄子令：商务域整段前置，⛔ 被角色递约插入中间）
        if (endorsementSystem.noticeQueue.length) {
            const lines: DialogLine[] = [];
            for (let n = endorsementSystem.consumeNotice(); n; n = endorsementSystem.consumeNotice()) {
                lines.push({ speaker: this.managerSpeaker, text: n.text });
            }
            this.assistPanel.playLines(lines, () => this.deliverStep());
            return;
        }
        // 商务待签：经纪人 VN 点评 → 重档弹窗（品牌/品类/title/年限/报酬）→ 两键强制二选一
        if (this.deliverBrandOffer()) return;

        // ② 综艺：录制完成结算通报（小助理）→ 剧宣待排期
        if (varietyPromoSystem.noticeQueue.length) {
            const lines: DialogLine[] = [];
            for (let n = varietyPromoSystem.consumeNotice(); n; n = varietyPromoSystem.consumeNotice()) {
                lines.push({ speaker: this.assistantSpeaker, text: assistantSystem.joinTerm(n.text) });
            }
            this.assistPanel.playLines(lines, () => this.deliverStep());
            return;
        }
        // 剧宣待排期（2026-10-31 批，七改全形态统一）：小助理 VN → 选周弹窗（剧组配合宣传口径）
        if (this.deliverPromoAsk()) return;

        // ③ 戏约：直签待签 offer → 经纪人 VN 一段 → 重档签约弹窗两键
        if (this.deliverDirectOffer()) return;
        // ④ 5c 竞角队列（决策页背景版）：月末快照存在才弹，全弹完跑收口夜乙段（派角）再收尾
        if ((this.panels.monthProgress as MonthProgressPanel).consumeRevealIfPending()) return;
        this.finishDeliver();
    }

    /** 直签送达链：经纪人 VN 一段（speechOf 出词）→ 重档签约弹窗两键。无待签返回 false 交给下一类 */
    private deliverDirectOffer(): boolean {
        const pending = directSignSystem.pendingOffer();
        if (!pending) return false;
        const contract = pending.contract;
        const script = GameData.SCRIPT_MAP[contract.scriptId];
        const role = script?.roles[contract.roleIndex];
        if (!script || !role) {
            // 坐标失效（剧本被删）：签不了也放弃不了 ⇒ 静默作废，接着消费下一类
            scriptSystem.voidDirectOffer(contract.id);
            return true;
        }
        const label = role.name ? `${role.roleType}「${role.name}」` : role.roleType;
        // 合同是否还挂着：别处作废/已处理过 ⇒ 按已完成处置（⛔ 卡在弹窗里出不去）
        const alive = (): boolean => {
            const c = GameState.getInstance().player.contracts.find(x => x.id === contract.id);
            return !!c && c.status === 'pending';
        };
        this.assistPanel.playLines([{ speaker: this.managerSpeaker, text: directSignSystem.speechOf(contract) }], () => {
            this.signPop.open({
                tier: 'heavy',
                content: roleCardHtml(script, role, { roleTitle: `直签角色：${label}`, dimUnmetReq: true }),
                buttons: [
                    {
                        label: '放弃', ghost: true,
                        run: () => {
                            if (!alive()) return true;
                            if (!scriptSystem.declineDirectOffer(contract.id)) return false;
                            EventBus.emit(GameEvents.STATE_CHANGED);
                        },
                    },
                    {
                        label: '签约',
                        run: () => {
                            if (!alive()) return true;
                            // 拒签只剩通告槽满：喊出来并原地留着，⛔ 静默吞掉玩家的点击
                            if (!scriptSystem.signDirectOffer(contract.id)) {
                                showHint('通告名额已满，先推掉一部再签');
                                return false;
                            }
                            EventBus.emit(GameEvents.STATE_CHANGED);
                            // 签约成功由通告列表/剧本状态表达，不再弹「签下了」
                        },
                    },
                ],
                onResolved: () => this.deliverStep(),
            });
        });
        return true;
    }

    /** 商务送达链（丙工位 2026-10-30）：VN 点评 → 重档弹窗两键。签约成功回执同样走经纪人 VN（2026-10-30 茄子令） */
    private deliverBrandOffer(): boolean {
        const cur = endorsementSystem.currentOffer();
        if (!cur) return false;
        const { offer, brand } = cur;
        this.assistPanel.playLines([{ speaker: this.managerSpeaker, text: offer.brokerLine }], () => {
            let signedLine: string | null = null;
            this.signPop.open({
                tier: 'heavy',
                content: brandOfferHtml(offer, brand),
                buttons: [
                    {
                        label: '放弃', ghost: true,
                        run: () => {
                            const r = endorsementSystem.reject();
                            if (!r.ok) return false;
                            EventBus.emit(GameEvents.STATE_CHANGED);
                        },
                    },
                    {
                        label: '签约',
                        run: () => {
                            const r = endorsementSystem.accept();
                            // 递约阶段已滤掉签不了的单，此处只剩状态竞态兜底：留卡说原因
                            if (!r.ok) { showHint(r.detail ?? '这单签不了'); return false; }
                            EventBus.emit(GameEvents.STATE_CHANGED);
                            signedLine = r.detail ?? '签下了。';
                        },
                    },
                ],
                onResolved: () => {
                    if (!signedLine) { this.deliverStep(); return; }
                    const line = signedLine;
                    signedLine = null;
                    this.assistPanel.playLines([{ speaker: this.managerSpeaker, text: line }], () => this.deliverStep());
                },
            });
        });
        return true;
    }

    /** 剧宣送达链（2026-10-31 批，同日七改全形态统一）：小助理 VN 说事（剧组请你配合宣传口径）→
     *  选周弹窗（周行占用置灰弹原因，点周即定档；婉拒键=不冷却不拉黑）。经纪人不掺手——剧宣行程
     *  归小助理（分工：经纪人=对外商务、助理=对内日程）。到周自动赴约 VN 与月结结算共用既有通道 */
    private deliverPromoAsk(): boolean {
        const cur = varietyPromoSystem.currentOffer();
        if (!cur) return false;
        const { offer, formName } = cur;
        this.assistPanel.playLines([{ speaker: this.assistantSpeaker, text: assistantSystem.joinTerm(offer.brokerLine) }], () => {
            this.signPop.open({
                tier: 'heavy',
                content: this.promoWeekPickerHtml(offer, formName),
                buttons: [
                    {
                        label: '婉拒', ghost: true,
                        run: () => {
                            const r = varietyPromoSystem.reject();
                            if (!r.ok) return false;
                            EventBus.emit(GameEvents.STATE_CHANGED);
                        },
                    },
                ],
                // 婉拒钮经 makeButton 走 onResolved；点周行在 picker 内手动 close+续链（见 promoWeekPickerHtml）
                onResolved: () => this.deliverStep(),
            });
        });
        return true;
    }

    /** 选周弹窗内容（全形态）：形式名+宣传月+宣传作品 → 收益 → 四行周次（占用置灰可点弹原因，点空周即定档） */
    private promoWeekPickerHtml(offer: PendingVarietyOffer, formName: string): HTMLElement {
        const conf = GameConfig.VARIETY_PROMO[offer.showId];
        const drama = GameData.SCRIPT_MAP[offer.scriptId];
        const y = Math.floor((offer.eventMonth - 1) / 12);
        const occupied = new Set(endorsementSystem.occupiedEventWeeks(offer.eventMonth));
        // promo-ask 修饰类：figure 收窄到 48%——50%×2 在半像素舍入下会把通告费挤换行到第三条线，
        // 左右两列行距因此不齐（2026-11-01 实机+几何探针实锤）
        const root = createDiv('brand-offer promo-ask');

        const head = createDiv('bo-head');
        const nameRow = createDiv('bo-name-row');
        nameRow.appendChild(createSpan('bo-name', formName));
        nameRow.appendChild(createInfoTip('行程那周占自由日（与周末安排互斥），收益在行程月的月结到账。婉拒不拉黑，下部剧杀青还会来。'));
        head.appendChild(nameRow);
        root.appendChild(head);

        const meta = createDiv('bo-meta');
        meta.appendChild(boRow('形式', conf?.label || '综艺通告'));
        meta.appendChild(boRow('宣传月', `第${y + 1}年${offer.eventMonth - y * 12}月`));
        meta.appendChild(boRow('宣传作品', drama ? drama.name : ''));
        root.appendChild(meta);

        // 收益块挂进 meta 网格（第二行右列，与「宣传作品」同行对齐）——
        // 旧版是 meta 之后的独立块，永远排在全部 meta 行下面 ⇒ 右列第二行行距比左列大 30px（几何探针实锤）
        const figure = createDiv('bo-figure');
        const amountRow = createDiv('bo-amount-row');
        if ((conf?.fee ?? 0) > 0) {
            figure.appendChild(createSpan('bo-k', '通告费'));
            amountRow.appendChild(createSpan('bo-amount', `¥${Utils.formatMoney(offer.fee)}`));
        } else {
            // 路演/专访=宣发行程，不结钱：收益用热度/名气表达（数值即 GameConfig 终值）
            figure.appendChild(createSpan('bo-k', '宣传收益'));
            const gains: string[] = [];
            if ((conf?.heat ?? 0) > 0) gains.push(`热度+${conf.heat}`);
            if ((conf?.fame ?? 0) > 0) gains.push(`名气+${conf.fame}`);
            amountRow.appendChild(createSpan('bo-amount', gains.join('　')));
        }
        figure.appendChild(amountRow);
        meta.appendChild(figure);

        const weeks = createDiv('bo-weeks');
        ['一', '二', '三', '四'].forEach((cn, i) => {
            const week = i + 1;
            const taken = occupied.has(week);
            const btn = createBtn(`bo-week${taken ? ' dim' : ''}`, [`第${cn}周`]);
            btn.addEventListener('click', () => {
                if (taken) { showHint('这一周已经有安排了'); return; }
                const r = varietyPromoSystem.accept(week);
                if (!r.ok) { showHint(r.detail ?? '排不进去'); return; }
                EventBus.emit(GameEvents.STATE_CHANGED);
                // ⛔ close() 会清 onResolved——选周路径在此手动续链（婉拒钮走 makeButton 自动续）
                this.signPop.close();
                this.deliverStep();
            });
            weeks.appendChild(btn);
        });
        root.appendChild(weeks);
        return root;
    }

    /** 送达链收尾：放开重入闸，兑现续动作（只兑一次） */
    private finishDeliver(): void {
        this.deliverBusy = false;
        const done = this.deliverDone;
        this.deliverDone = null;
        done?.();
    }

    /** 当前可见面板 */
    get currentPanel(): PanelName | null {
        return this.current;
    }

    /** 显隐底部导航（聊天子视图内隐藏，返回列表恢复） */
    setTabBarVisible(visible: boolean): void {
        this.tabBar.classList.toggle('hidden', !visible);
    }

    /** 底部导航点击：行程/人脉/微博 → 对应页面 */
    private onTabClick(key: string): void {
        if (key === 'decision' || key === 'characters' || key === 'weibo') {
            this.show(key as PanelName);
        }
    }

    /** 高亮导航项（月进度归属「行程」；事件弹窗为覆盖层不改动） */
    private setActiveTab(name: PanelName): void {
        const key = name === 'monthProgress' || name === 'awardCeremony' || name === 'stageStory' ? 'decision' : name;
        Object.values(this.tabItems).forEach(t => t.classList.remove('active'));
        const item = this.tabItems[key];
        if (item && !HIDE_TAB_BAR.has(name)) item.classList.add('active');
    }

    /** 全局事件接线 */
    private wireEvents(): void {
        // 状态变更同步人脉未读红点与微博剧宣角标
        EventBus.on(GameEvents.STATE_CHANGED, () => {
            this.updateCharactersBadge();
            this.updateWeiboBadge();
        });
        // 周目结束 → 结算页
        EventBus.on(GameEvents.GAME_OVER, (_reason: string, _finalRank: number) => {
            // 确保执行循环停止
            gameLoop.reset();
            // 触发结局即视为该周目终结：删除存档，主页「继续游戏」随之置灰，无法再读档续玩
            saveSystem.deleteSave();
            // 商店解锁 / 随机池解锁 / 结算快照已收口至 EndingSystem.endGame 统一写 meta（结局批 2026-09-29）
            this.show('gameOver');
        });
        // 月度执行弹窗结束后由 MonthProgressPanel.finish() 显式返回决策日
    }

    /** 开始新游戏流程：重置 → 创建角色 → 跳转开局 */
    onNewGame(): void {
        gameManager.reset();
        gameLoop.reset();
        // 面板是进程内单例，数值宿主跨局复用：不清基线，新开局会看到数字从上一位的值滚过来
        resetFeedback();
        // 用户 10-05 令「完成创建角色再删旧档存新档」：点这里不许碰磁盘——旧档退位点
        // = 捏脸页「确认创建」里 CharacterCreatePanel.confirm() 的 saveSystem.save()
        // （A/B 双键轮换 10-13：那次写盘落进较旧一格，旧档就此退成备份，再被下一次写顶掉）。
        // 创建中途退出/关页 ⇒ 旧档原样可「继续游戏」（内存档被 reset 无妨，读档会覆盖回来）。
        // 用户 10-03 令「开新档用默认位置」：入口落位是设备级偏好、⛔ 属于存档，deleteSave() 清不到它，
        // 而内联 left/top 是 init 时就还原上去的 ⇒ 光清键本局仍停在旧点，必须连内联一起交回 CSS 默认
        StorageAdapter.removeItem(FAB_POS_KEY);
        this.fabReset?.();
        gameManager.newGame();
        // 应用商店购买的开局加成（咖位 + 属性）
        shopSystem.applyStartRank();
        shopSystem.applyStartAttributes();
        this.show('characterCreate');
    }

    /** 继续游戏流程 */
    async onContinue(): Promise<void> {
        const state = gameManager.continueGame();
        // 无档时主菜单「继续游戏」已置灰（MainMenuPanel.refresh 的 disabled）——此处只做流程兜底，不再弹提示
        if (!state) return;
        // 顶栏主角头像先出图再切页（2026-10-29 §3.4cm 三轮）：否则决策页第一帧会露白底再换脸
        await avatarPngSrc(state.player.avatar).catch(() => '');
        this.show('decision');
    }

    /** 返回主菜单（周目结算后） */
    onBackToMenu(): void {
        this.show('mainMenu');
    }
}

/** 全局单例 */
export const gameApp = new GameApp();

// D-03⑤「钉角」的 --foldY 捕获期 scroll 监听随 2026-10-23 折角删除一并撤下（它唯一的消费者就是那条 transform）。

// C-06（48 批）：`:has()` 兼容收口——低版本 WebView 不支持 :has()，三条跨子树样式改挂 #app 镜像类。
// 一个观察者统一同步（class 变动/节点增删都触发，三个 querySelector 判据成本可忽略），
// CSS 侧：.month-open（月度弹窗遮罩亮）/ .tabbar-hidden（页签带藏）/ .modal-open（任一真弹窗开）。
// overlay-open（2026-10-28 茄子抓「有的弹窗在悬浮入口上有的在下」）：modal-open 只认 .modal-wrap.show，
// RoleDetailPop（直签/剧本册详情）与竞角队列弹窗都是裸 dimoverlay，漏在网外——
// 独立镜像类罩住全部覆盖层形状，小助理入口一律让位（禁止并进 modal-open：那两条既有消费者
// z-month-top 降位/vn-dialog 锁点会跟着误伤）。
// 2026-11-02：5c 废页后判据里的 `.reveal-overlay.show` 随整页 CSS 一并撤下（后继弹窗共用
// .reveal-detail-overlay，仍被下一条覆盖）。
const appRoot = document.getElementById('app');
if (appRoot) {
    const syncAppFlags = () => {
        appRoot.classList.toggle('month-open', !!appRoot.querySelector('.month-progress-root .dimoverlay.show'));
        appRoot.classList.toggle('tabbar-hidden', !!appRoot.querySelector('.tab-bar.hidden'));
        appRoot.classList.toggle('modal-open', !!appRoot.querySelector('.modal-wrap.show'));
        appRoot.classList.toggle('overlay-open',
            !!appRoot.querySelector('.modal-wrap.show, .reveal-detail-overlay.show'));
    };
    new MutationObserver(syncAppFlags).observe(appRoot, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['class'],
    });
    syncAppFlags();
}

// 供端到端冒烟测试注入状态（window.localStorage 为只读 getter，测试无法直接取到单例；
// 用惰性 getter 保证始终拿到 reset 后的活跃单例）
Object.defineProperty(globalThis, '_gameRef', {
    get: () => GameState.getInstance(),
    configurable: true,
});
// 静态数据表同样暴露一份给冒烟测试断言（NPC 文案等）
Object.defineProperty(globalThis, '_gameData', {
    get: () => GameData,
    configurable: true,
});
// 剧本系统 / 普通 NPC 系统同样暴露给端到端测试（回归守卫：签约结识同剧组 NPC 的精确性）
Object.defineProperty(globalThis, '_scriptSystem', {
    get: () => scriptSystem,
    configurable: true,
});
Object.defineProperty(globalThis, '_normalNpcSystem', {
    get: () => normalNpcSystem,
    configurable: true,
});
// 经纪人通知系统暴露给端到端测试（回归守卫：通知 → 小助理播报链路走真实路径，不 bypass 事件）
Object.defineProperty(globalThis, '_noticeSystem', {
    get: () => noticeSystem,
    configurable: true,
});
// 小助理系统暴露给端到端测试（未读快照断言）
Object.defineProperty(globalThis, '_assistantSystem', {
    get: () => assistantSystem,
    configurable: true,
});
// 奖项系统暴露给端到端测试（提名公布 → 小助理播报链路走真实路径）
Object.defineProperty(globalThis, '_awardSystem', {
    get: () => awardSystem,
    configurable: true,
});
// 商务代言系统暴露给端到端测试（送达链/noticeQueue 注入验收）
Object.defineProperty(globalThis, '_endorsementSystem', {
    get: () => endorsementSystem,
    configurable: true,
});
// GameApp 暴露给端到端测试（直达各面板实例，如 5c 竞角揭晓整页验收；仅测试钩子）
Object.defineProperty(globalThis, '_gameApp', {
    get: () => gameApp,
    configurable: true,
});
// 音频系统暴露给端到端测试（批 1 解锁链路验收：_audioManager.isUnlocked() / playSfx 静默不报错）
Object.defineProperty(globalThis, '_audioManager', {
    get: () => audioManager,
    configurable: true,
});

// 调试帮手（2026-10-27 茄子令）：F12 控制台自由改数据。键 = window._dbg，惰性 getter
// 保证拿到活跃单例；正式包同带（与上面 _gameRef 一族同性质），不进任何 UI。
Object.defineProperty(globalThis, '_dbg', {
    get: () => {
        const state = GameState.getInstance();
        const sync = () => EventBus.emit(GameEvents.STATE_CHANGED);
        // player/阶段表在每次调用时现取——主菜单上还没有 player，getter 期捕获会拿到 undefined
        return {
            /** 打印当前状态速查（阶段/审查条件/常用字段） */
            info() {
                const p = state.player;
                if (!p) return console.warn('还没开新游戏，player 不存在');
                const stage = GameData.getStageById(state.currentStage);
                const lines = [
                    `阶段 ${state.currentStage}：${stage?.startAge}~${stage ? stage.endAge - 1 : '?'} 岁，审查线 Lv.${stage?.requiredRank}`,
                    `年龄 ${p.age} / 年 ${p.year} / 月 ${p.month} / 周 ${p.week}`,
                    `咖位 Lv.${p.rank} / 名气 ${p.fame} / 金钱 ${p.resources.money} / 口碑 ${p.attributes.reputation}`,
                ];
                console.log(lines.join('\n'));
                console.log('改数据：age / month / year / rank 直接赋值，或用 _dbg.attr(key, v)、money(v)、fame(v)、rep(v)');
                console.log('审查测试：_dbg.review("fail") 或 _dbg.review("pass")——改完玩完本月，月末自动判定');
            },
            /** 审查测试一键布置：挪到阶段末年 2 月并把咖位调到判定线的上/下方，玩完本月即判定 */
            review(mode: 'pass' | 'fail' = 'fail') {
                const p = state.player;
                const stage = GameData.getStageById(state.currentStage);
                if (!p || !stage) return console.warn('还没开新游戏，player 不存在');
                p.age = stage.endAge;
                p.month = 2;
                p.rank = mode === 'pass' ? stage.requiredRank : Math.max(1, stage.requiredRank - 1);
                // pass 回响（FIXED_REVIEW_1_PASS）有 requireFlags: [FLAG_MOTHER_VOW] 前置，而立约戏
                // 要求 rankMax:2——测试直跳永远没立过约，标记必须由这里补上，否则判定过后没剧情
                if (mode === 'pass') state.setFlag('FLAG_MOTHER_VOW');
                sync();
                console.log(`已布置：${p.age} 岁 2 月、咖位 Lv.${p.rank}（线=Lv.${stage.requiredRank}）。玩完本月，月末决策日判定。`);
            },
            attr(key: string, value: number) { state.setAttribute(key as AttributeKey, value); },
            money(v: number) { state.setMoney(v); },
            fame(v: number) { state.setFame(v); },
            /** 手动派发固定事件（如审查失败剧情），成功返回 true */
            plot(eventId: string) { return PlotSystem.getInstance().fireByEvent(eventId); },
            flag(id: string, v: boolean = true) { state.setFlag(id, v); },
            /** 立即存档（改完数据落盘，刷新不丢） */
            save() { return saveSystem.save(); },
        };
    },
    configurable: true,
});

// 音频：授权层（首次手势解锁探针）+ 切后台/回前台策略。须在首个 show 之前挂载——
// 首个 show('mainMenu') 发生在任何手势之前，音频层此时只记 pending、不尝试播放
audioManager.init();
gameApp.show('mainMenu');
// 头像部件在空闲期 low 优先级预取：玩家第一次看到头像位时就是成品，不闪 emoji 兜底（2026-10-29 §3.4cm）
warmupAvatarParts();
// 小地图三屏底图预取（周末惰性构造，提前备好字节；2026-10-29 §3.4cm 四轮）
prefetchFreeroamBgs();

/**
 * 商务重档弹窗内容（2026-10-30 用户令改版）：品牌名+说明问号 → 两列四行读数 → 费用大字。
 * 信息层次：档位 / 品类 / 身份 / 合约期·活动档期（两列两行）；费用落底＝年费 × 合约年限
 * （挚友＝单次活动费、当天结）。**无底块**：全部直接落在纸卡上（2026-10-30 用户令去薄荷块）。
 * 版式类 = `.brand-offer`（ui.css，专用于本弹窗），⛔ 再借剧本详情的 pd-* 族。
 * 说明（后果提示）走 info-tip 气泡，⛔ 不再常驻占一栏。
 */
function brandOfferHtml(offer: PendingBrandOffer, brand: BrandDef): HTMLElement {
    const isFriend = offer.title === 'friend';
    const totalFee = isFriend ? offer.fee : offer.fee * offer.years;
    const root = createDiv('brand-offer');

    const head = createDiv('bo-head');
    const nameRow = createDiv('bo-name-row');
    nameRow.appendChild(createSpan('bo-name', brand.name));
    nameRow.appendChild(createInfoTip(isFriend
        ? '为期一周的单次品牌活动。拒签后，这家品牌五年内不再递约。'
        : '同品类限签一家。名气连续六个月低于品牌要求，品牌方会按合同解约，违约金为一年的代言费。拒签后，这家品牌五年内不再递约。'));
    head.appendChild(nameRow);
    root.appendChild(head);

    const meta = createDiv('bo-meta');
    meta.appendChild(boRow('档位', `${BRAND_TIER_NAMES[brand.tier]}品牌`));
    meta.appendChild(boRow('品类', BRAND_CATEGORY_NAMES[brand.category] ?? ''));
    meta.appendChild(boRow('身份', ENDORSE_TITLE_NAMES[offer.title]));
    meta.appendChild(isFriend
        ? boRow('期限', '本月内完成')
        : boRow('合约期', `${offer.years} 年`));
    root.appendChild(meta);

    const figure = createDiv('bo-figure');
    figure.appendChild(createSpan('bo-k', isFriend ? '活动费用' : '费用'));
    const amountRow = createDiv('bo-amount-row');
    amountRow.appendChild(createSpan('bo-amount', `¥${Utils.formatMoney(totalFee)}`));
    figure.appendChild(amountRow);
    root.appendChild(figure);
    return root;
}

/** 信息区一行读数（键左值右，键固定 3em、值在剩余空隙居中；两列由 CSS 各占 50%） */
function boRow(k: string, v: string): HTMLElement {
    const row = createDiv('bo-row');
    row.appendChild(createSpan('bo-k', k));
    row.appendChild(createSpan('bo-v', v));
    return row;
}
